import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { message, Spin } from 'antd';
import { useAuth } from '../../auth.js';
import {
  buildMappingExemplarQueue,
  confirmMapping,
  fetchGeometrySheets,
  publishMappingTemplate,
  ruleLabel,
} from '../../services/drawingGeometryApi';
import { TAXONOMY_AXIS_OPTIONS } from '../../constants/pilotDrawingKinds';
import { DIMENSIONAL_OUTPUT_KINDS_LC, TAXONOMY_STATS } from '../../constants/drawingTaxonomy';
import KindSearchSelect from './KindSearchSelect';
import { DISCIPLINE_HATS } from '../../constants/disciplineHats.js';

const PanelRoot = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
`;

const PanelHeader = styled.div`
  padding: 20px 20px 16px;
  border-bottom: 1px solid rgba(0,0,0,0.1);
`;

const Workflow = styled.ol`
  margin: 14px 0 0;
  padding: 0;
  list-style: none;
  display: grid;
  gap: 8px;
`;

const WorkflowStep = styled.li`
  display: flex;
  gap: 10px;
  align-items: flex-start;
  font-size: 13px;
  line-height: 1.45;
  color: ${(p) => (p.$done ? '#047857' : p.$active ? '#5c3d1e' : '#6b7280')};

  strong {
    color: ${(p) => (p.$active ? '#e97024' : 'inherit')};
  }
`;

const StepNum = styled.span`
  flex-shrink: 0;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  display: grid;
  place-items: center;
  font-size: 11px;
  font-weight: 700;
  background: ${(p) => (p.$done ? '#d1fae5' : p.$active ? '#ffefe7' : '#f3f4f6')};
  color: ${(p) => (p.$done ? '#047857' : p.$active ? '#e97024' : '#9ca3af')};
`;

const Stats = styled.div`
  display: flex;
  gap: 10px;
  margin-top: 14px;
  flex-wrap: wrap;
`;

const Stat = styled.div`
  text-align: center;
  padding: 10px 12px;
  background: rgba(255,255,255,0.8);
  border-radius: 8px;
  border: 1px solid rgba(0,0,0,0.1);
  min-width: 76px;
  flex: 1;
`;

const DetectedRow = styled.div`
  margin-top: 12px;
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;

const KindChip = styled.span`
  font-size: 11px;
  font-weight: 600;
  padding: 4px 8px;
  border-radius: 999px;
  background: rgba(233,112,36,0.1);
  color: #9a3412;
`;

const HatBar = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
  padding-bottom: 12px;
  border-bottom: 1px solid rgba(0,0,0,0.07);
`;

const HatLabel = styled.label`
  font-size: 12px;
  font-weight: 600;
  color: #888;
  white-space: nowrap;
`;

const HatSelect = styled.select`
  flex: 1;
  height: 30px;
  padding: 0 8px;
  border: 1.5px solid #e1e5e9;
  border-radius: 7px;
  background: #fff;
  font-size: 12px;
  color: #333;
  cursor: pointer;
  &:focus { outline: none; border-color: #e97024; }
`;

const ActionBox = styled.div`
  margin-top: 14px;
  padding: 12px;
  border-radius: 10px;
  background: rgba(233,112,36,0.06);
  border: 1px solid rgba(233,112,36,0.18);
`;

const ActionTitle = styled.div`
  font-size: 13px;
  font-weight: 700;
  color: #333;
`;

const ActionHint = styled.div`
  font-size: 12px;
  color: #666;
  margin-top: 4px;
  line-height: 1.45;
`;

const List = styled.div`
  padding: 0 20px 20px;
`;

const Item = styled.div`
  padding: 16px;
  border-radius: 12px;
  margin-bottom: 12px;
  background: ${(p) => (p.$selected ? 'rgba(233,112,36,0.06)' : 'rgba(255,255,255,0.9)')};
  border: 2px solid ${(p) => (p.$selected ? '#e97024' : 'rgba(0,0,0,0.08)')};
  box-shadow: 0 2px 8px rgba(0,0,0,0.04);
  cursor: ${(p) => (p.$clickable ? 'pointer' : 'default')};
  transition: border-color 0.15s, background 0.15s;
`;

const Row = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 10px;
  flex-wrap: wrap;
  align-items: center;
`;

const AutoGuessHint = styled.div`
  font-size: 11px;
  color: #b45309;
  margin-top: 6px;
  line-height: 1.4;
`;

const Btn = styled.button`
  height: 36px;
  padding: 0 14px;
  border-radius: 8px;
  border: none;
  font-weight: 600;
  font-size: 13px;
  cursor: pointer;
  background: ${(p) => (p.$primary ? '#e97024' : 'rgba(233,112,36,0.12)')};
  color: ${(p) => (p.$primary ? '#fff' : '#e97024')};
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const Empty = styled.div`
  padding: 32px 20px;
  color: #666;
  font-size: 14px;
  line-height: 1.55;
`;

const Badge = styled.span`
  display: inline-block;
  font-size: 11px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 999px;
  background: rgba(233,112,36,0.12);
  color: #c45f12;
  margin-left: 8px;
`;

const ConfirmedList = styled.ul`
  margin: 8px 0 0;
  padding: 0;
  list-style: none;
  font-size: 12px;
  color: #047857;
  display: grid;
  gap: 4px;
`;

function defaultAxisForKind(kind) {
  if (!kind) return 'reference_annotation';
  if (DIMENSIONAL_OUTPUT_KINDS_LC.has(kind.toLowerCase())) return 'dimensional_output';
  return 'reference_annotation';
}

function countDetectedByKind(sheets) {
  const counts = new Map();
  for (const sheet of sheets || []) {
    for (const comp of sheet.ir?.components || []) {
      const kind = comp.kind || 'unknown';
      counts.set(kind, (counts.get(kind) || 0) + 1);
    }
  }
  return Array.from(counts.entries()).sort((a, b) => b[1] - a[1]);
}

export default function MappingCheckPanel({
  projectId,
  requirementId,
  firmId,
  hatId = 'architect',
  onHatChange,
  publishedBy: publishedByProp = '',
  onSelectComponent,
  onSheetsLoaded,
}) {
  const auth = useAuth();
  const publishedBy = publishedByProp || auth?.user?.profile?.email || '';
  const [loading, setLoading] = useState(false);
  const [sheets, setSheets] = useState([]);
  const [confirmedRules, setConfirmedRules] = useState({});
  const [draftKinds, setDraftKinds] = useState({});
  const [publishing, setPublishing] = useState(false);
  const [previewRuleKey, setPreviewRuleKey] = useState(null);
  const [lastPublishedVersion, setLastPublishedVersion] = useState(null);

  const loadSheets = useCallback(async () => {
    if (!projectId || !requirementId) {
      setSheets([]);
      return;
    }
    setLoading(true);
    try {
      const data = await fetchGeometrySheets(projectId, requirementId, { includeIr: true });
      const loaded = (data.sheets || []).map((s) => ({
        irKey: s.irKey,
        sheetId: s.sheetId,
        url: s.irSummaryUrl || s.irUrl,
        ir: s.ir,
        entityPages: s.entityPages || [],
        sourcePdfKey: s.sourcePdfKey,
        sourcePdfUrl: s.sourcePdfUrl,
      }));
      setSheets(loaded);
      onSheetsLoaded?.(loaded);
    } catch (err) {
      console.error('[MappingCheckPanel] load error:', err);
      message.error(err.message || 'Failed to load drawing IR');
      setSheets([]);
    } finally {
      setLoading(false);
    }
  }, [projectId, requirementId, onSheetsLoaded]);

  useEffect(() => {
    loadSheets();
  }, [loadSheets]);

  const exemplarQueue = useMemo(() => {
    const all = buildMappingExemplarQueue(sheets);
    return all.filter((b) => !confirmedRules[b.ruleKey]);
  }, [sheets, confirmedRules]);

  const totalInstances = useMemo(
    () => exemplarQueue.reduce((n, b) => n + b.instanceCount, 0),
    [exemplarQueue]
  );

  const detectedKinds = useMemo(() => countDetectedByKind(sheets), [sheets]);

  const stats = useMemo(() => ({
    sheets: sheets.length,
    rules: exemplarQueue.length,
    instances: totalInstances,
    confirmed: Object.keys(confirmedRules).length,
  }), [sheets.length, exemplarQueue.length, totalInstances, confirmedRules]);

  const workflowStep = stats.rules > 0 ? 1 : stats.confirmed > 0 ? 2 : 0;
  const readyToApply = stats.rules === 0 && stats.confirmed > 0;

  const lastAutoPreviewRef = useRef(null);

  useEffect(() => {
    if (!onSelectComponent || exemplarQueue.length === 0) {
      setPreviewRuleKey(null);
      lastAutoPreviewRef.current = null;
      return;
    }
    const stillVisible = previewRuleKey && exemplarQueue.some((b) => b.ruleKey === previewRuleKey);
    if (stillVisible) return;
    const first = exemplarQueue[0];
    const autoKey = `${first.ruleKey}:${first.exemplar?.id}`;
    if (lastAutoPreviewRef.current === autoKey) return;
    lastAutoPreviewRef.current = autoKey;
    const sheet = sheets.find((s) => s.irKey === first.exemplar?.irKey);
    setPreviewRuleKey(first.ruleKey);
    onSelectComponent({ ...first, sheet });
  }, [exemplarQueue, previewRuleKey, onSelectComponent, sheets]);

  const handlePreview = useCallback((bucket) => {
    lastAutoPreviewRef.current = `${bucket.ruleKey}:${bucket.exemplar?.id}`;
    setPreviewRuleKey(bucket.ruleKey);
    const sheet = sheets.find((s) => s.irKey === bucket.exemplar?.irKey);
    onSelectComponent?.({ ...bucket, sheet });
  }, [onSelectComponent, sheets]);

  const handleConfirm = async (bucket) => {
    const kind = draftKinds[bucket.ruleKey];
    if (!kind) {
      message.warning('Search and pick the correct label type before confirming.');
      return;
    }
    const exemplar = bucket.exemplar;
    const taxonomyAxis = defaultAxisForKind(kind);
    try {
      const result = await confirmMapping({
        projectId,
        requirementId,
        irKey: exemplar.irKey,
        componentId: exemplar.id,
        matchedRule: bucket.matchedRule,
        kind,
        taxonomyAxis,
        firmId: firmId || projectId,
        hatId,
      });
      setConfirmedRules((prev) => ({
        ...prev,
        [bucket.ruleKey]: { kind, taxonomyAxis, instances: result.instancesConfirmed },
      }));
      await loadSheets();
      message.success(
        `Saved "${kind}" for ${result.instancesConfirmed || bucket.instanceCount} `
        + `matching label${(result.instancesConfirmed || bucket.instanceCount) === 1 ? '' : 's'} on this drawing set.`
      );
    } catch (err) {
      console.error('[MappingCheckPanel] confirm error:', err);
      message.error(err.message || 'Failed to confirm mapping');
    }
  };

  const handlePublish = async () => {
    if (exemplarQueue.length > 0) {
      message.warning(`Confirm all ${exemplarQueue.length} label type${exemplarQueue.length === 1 ? '' : 's'} below before applying rules.`);
      return;
    }
    if (stats.confirmed === 0) {
      message.warning('Confirm at least one label type first.');
      return;
    }
    setPublishing(true);
    try {
      const result = await publishMappingTemplate({
        projectId,
        requirementId,
        firmId: firmId || projectId,
        hatId,
        publishedBy,
      });
      setLastPublishedVersion(result.version);
      message.success(
        `Labeling rules v${result.version} saved — applied to `
        + `${result.componentsLabeled || 0} labels across ${result.sheetsUpdated || 0} sheets.`
      );
      await loadSheets();
    } catch (err) {
      console.error('[MappingCheckPanel] publish error:', err);
      message.error(err.message || 'Failed to apply labeling rules');
    } finally {
      setPublishing(false);
    }
  };

  if (!projectId) {
    return <Empty>Select a project to run Mapping Check.</Empty>;
  }

  if (!requirementId) {
    return (
      <Empty>
        Open Mapping Check from a linked drawing folder, or pass <code>requirementId</code> in review navigation state.
      </Empty>
    );
  }

  return (
    <PanelRoot>
      <PanelHeader>
        <div style={{ fontSize: 17, fontWeight: 700, color: '#333' }}>Teach CODiii your labels</div>
        <div style={{ color: '#666', marginTop: 6, fontSize: 13, lineHeight: 1.5 }}>
          CODiii auto-detects labels on your drawings. For anything it is unsure about, you confirm
          one example per type — not every instance. Those confirmations become
          {' '}
          <strong>labeling rules</strong>
          {' '}
          that apply across every sheet in this folder.
        </div>

        {onHatChange && (
          <HatBar style={{ marginTop: 14 }}>
            <HatLabel htmlFor="hat-select">Discipline</HatLabel>
            <HatSelect
              id="hat-select"
              value={hatId}
              onChange={(e) => onHatChange(e.target.value)}
            >
              {DISCIPLINE_HATS.map((h) => (
                <option key={h.id} value={h.id}>{h.label}</option>
              ))}
            </HatSelect>
          </HatBar>
        )}

        <Workflow>
          <WorkflowStep $active={workflowStep === 1} $done={workflowStep > 1}>
            <StepNum $active={workflowStep === 1} $done={workflowStep > 1}>1</StepNum>
            <span>
              <strong>Review each label type</strong>
              {' '}
              — click a row to preview it on the drawing, pick the correct type, then confirm.
            </span>
          </WorkflowStep>
          <WorkflowStep $active={workflowStep === 2} $done={readyToApply && lastPublishedVersion}>
            <StepNum $active={workflowStep === 2} $done={readyToApply && lastPublishedVersion}>2</StepNum>
            <span>
              <strong>Apply rules to all sheets</strong>
              {' '}
              — once every type is confirmed, CODiii labels every matching instance automatically.
            </span>
          </WorkflowStep>
        </Workflow>

        <Stats>
          <Stat>
            <div style={{ fontSize: 20, fontWeight: 700, color: '#e97024' }}>{stats.sheets}</div>
            <div style={{ fontSize: 10, color: '#666', textTransform: 'uppercase', fontWeight: 600 }}>Sheets</div>
          </Stat>
          <Stat>
            <div style={{ fontSize: 20, fontWeight: 700, color: '#ef4444' }}>{stats.rules}</div>
            <div style={{ fontSize: 10, color: '#666', textTransform: 'uppercase', fontWeight: 600 }}>Types left</div>
          </Stat>
          <Stat>
            <div style={{ fontSize: 20, fontWeight: 700, color: '#10b981' }}>{stats.confirmed}</div>
            <div style={{ fontSize: 10, color: '#666', textTransform: 'uppercase', fontWeight: 600 }}>Types confirmed</div>
          </Stat>
        </Stats>

        {detectedKinds.length > 0 && (
          <DetectedRow>
            <span style={{ fontSize: 11, color: '#888', fontWeight: 600, alignSelf: 'center' }}>Detected:</span>
            {detectedKinds.slice(0, 8).map(([kind, count]) => (
              <KindChip key={kind}>{kind} ({count})</KindChip>
            ))}
          </DetectedRow>
        )}

        <ActionBox>
          <ActionTitle>
            {readyToApply
              ? 'Ready — apply your labeling rules'
              : stats.rules > 0
                ? `${stats.rules} label type${stats.rules === 1 ? '' : 's'} still need confirmation`
                : 'No labels detected yet'}
          </ActionTitle>
          <ActionHint>
            {readyToApply
              ? 'This saves your confirmed types as firm rules and bulk-labels every matching instance on all sheets in this folder. Future drawing uploads will reuse these rules.'
              : stats.rules > 0
                ? 'Work through the list below. Each row is one type (e.g. dimensions, room tags, callouts) — confirming one example teaches CODiii the pattern for all similar labels.'
                : 'Wait for extraction to finish, or check that PDFs are classified as Deliverable → Drawing on Link Folders.'}
          </ActionHint>
          <Row style={{ marginTop: 10 }}>
            <Btn type="button" onClick={loadSheets} disabled={loading}>Refresh</Btn>
            <Btn
              type="button"
              $primary
              onClick={handlePublish}
              disabled={loading || publishing || !readyToApply}
            >
              {publishing ? 'Applying rules…' : 'Apply rules to all sheets'}
            </Btn>
          </Row>
          {lastPublishedVersion != null && (
            <div style={{ fontSize: 11, color: '#047857', marginTop: 8, fontWeight: 600 }}>
              Last applied: labeling rules v{lastPublishedVersion}
            </div>
          )}
        </ActionBox>

        {Object.keys(confirmedRules).length > 0 && (
          <ConfirmedList>
            {Object.entries(confirmedRules).map(([ruleKey, info]) => (
              <li key={ruleKey}>
                ✓ {info.kind} — {info.instances || 0} labels ({ruleLabel(ruleKey)})
              </li>
            ))}
          </ConfirmedList>
        )}
      </PanelHeader>

      {loading ? (
        <div style={{ padding: 40, textAlign: 'center' }}><Spin /></div>
      ) : sheets.length === 0 ? (
        <Empty>
          No drawing sheets yet. On Link Folders, classify PDFs as <strong>Deliverable → Drawing</strong> and wait for extraction to finish.
        </Empty>
      ) : exemplarQueue.length === 0 && !readyToApply ? (
        <Empty>
          Nothing needs confirmation right now. If labels look wrong on the drawing, refresh after extraction completes or re-link the folder to re-run extract.
        </Empty>
      ) : exemplarQueue.length === 0 ? (
        <Empty>
          All types confirmed. Click <strong>Apply rules to all sheets</strong> above to bulk-label every matching instance.
        </Empty>
      ) : (
        <List>
          {exemplarQueue.map((bucket) => {
            const exemplar = bucket.exemplar;
            const draftKind = draftKinds[bucket.ruleKey] || '';
            const autoGuess = bucket.suggestedKind && bucket.suggestedKind !== 'unknown'
              ? bucket.suggestedKind
              : null;
            return (
              <Item
                key={bucket.ruleKey}
                $selected={previewRuleKey === bucket.ruleKey}
                $clickable={!!onSelectComponent}
                onClick={() => handlePreview(bucket)}
              >
                <div style={{ fontSize: 12, color: '#888', fontWeight: 600, textTransform: 'uppercase' }}>
                  {ruleLabel(bucket.matchedRule)}
                  <Badge>{bucket.instanceCount} similar</Badge>
                  {bucket.sheetIds.length > 1 && (
                    <Badge>{bucket.sheetIds.length} sheets</Badge>
                  )}
                </div>
                <div style={{ fontWeight: 600, marginTop: 6, color: '#333' }}>
                  Example: {exemplar.label || exemplar.value || exemplar.id}
                </div>
                <div style={{ fontSize: 12, color: '#666', marginTop: 4 }}>
                  Sheet {exemplar.sheetId}
                  {exemplar.pageIndex != null ? ` · page ${exemplar.pageIndex + 1}` : ''}
                  {exemplar.unknownReason ? ` — ${exemplar.unknownReason}` : ''}
                </div>
                {autoGuess && (
                  <AutoGuessHint>
                    CODiii auto-labeled this as &ldquo;{autoGuess}&rdquo; — search below for the correct type
                    {' '}
                    ({TAXONOMY_STATS.uniqueKindCount} types:
                    {' '}
                    {TAXONOMY_STATS.referenceKindCount} reference,
                    {' '}
                    {TAXONOMY_STATS.dimensionalKindCount} dimensional).
                  </AutoGuessHint>
                )}
                <Row onClick={(e) => e.stopPropagation()}>
                  <KindSearchSelect
                    value={draftKind}
                    onChange={(v) => setDraftKinds((prev) => ({ ...prev, [bucket.ruleKey]: v }))}
                  />
                  <span style={{ fontSize: 12, color: '#666' }}>
                    {draftKind
                      ? TAXONOMY_AXIS_OPTIONS.find((o) => o.value === defaultAxisForKind(draftKind))?.label
                      : 'Pick a type'}
                  </span>
                  <Btn type="button" $primary disabled={!draftKind} onClick={() => handleConfirm(bucket)}>
                    Confirm this type
                  </Btn>
                </Row>
              </Item>
            );
          })}
        </List>
      )}
    </PanelRoot>
  );
}
